import * as Icons from '@ant-design/icons';
import useNotification from 'hooks/useNotification';
import { Button, Flex, Form, Input, Modal, Space, Typography } from 'antd';
import { FieldsetTitle } from 'components/professor_area/FieldsetTitle';
import { FormItemCol } from 'components/professor_area/FormItemCol';
import { ICertificateEnrollment } from 'interfaces/course_certificate';
import { InputMask } from 'components/form-items/InputMask';
import { useCallback } from 'react';
import { useUpdate } from '@refinedev/core';
import { useCertificateStore } from '../../../certificate.store';
import { fullNameValidatorRule } from 'utils/validators';

interface ISectionUserInfoProps {
  user: ICertificateEnrollment['user'];
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
}

export const SectionUserInfo = ({
  user,
  isEditing,
  setIsEditing
}: ISectionUserInfoProps) => {
  const { mutateAsync, isLoading } = useUpdate();
  const [form] = Form.useForm();
  const { showError } = useNotification();
  const [modal, contextHolder] = Modal.useModal();
  const { enrollment } = useCertificateStore();

  const handleUpdateUserInfo = useCallback(() => {
    form
      .validateFields()
      .then(() => {
        mutateAsync({
          resource: 'users',
          id: user?.id,
          values: {
            email: form.getFieldValue('email'),
            personal_info_attributes: {
              full_name: form.getFieldValue(['personal_info', 'full_name']),
              phone_number: form.getFieldValue([
                'personal_info',
                'phone_number'
              ])
            }
          },
          successNotification: () => ({
            type: 'success',
            description: 'Sucesso!',
            message: 'Dados do usuário atualizados com sucesso!'
          }),
          errorNotification: () => ({
            type: 'error',
            description: 'Ops!',
            message: 'Não foi possível atualizar os dados do usuário.'
          })
        }).then(() => {
          setIsEditing(false);
        });
      })
      .catch(() => {
        showError('Erro ao corrigir questão.');
        setIsEditing(true);
      });
  }, [form, mutateAsync, showError, user?.id, setIsEditing]);

  const handleEdit = useCallback(async () => {
    setIsEditing(true);

    if (isEditing) {
      form
        .validateFields()
        .then(async () => {
          await modal.confirm({
            title: 'Confirmar edição de informações?',
            width: 600,
            content: (
              <Flex vertical gap={16}>
                <Typography.Text>
                  Ao editar esta informação, ela será atualizada em todas as
                  áreas do sistema/Admin onde estes dados do aluno são exibidos,
                  incluindo:
                </Typography.Text>
                <ul className="list-inside mb-0 pl-0">
                  <li>
                    <Typography.Text>Página do aluno</Typography.Text>
                  </li>
                  <li>
                    <Typography.Text>Página de documentações</Typography.Text>
                  </li>
                </ul>
                <Typography.Text>
                  Essa ação será registrada no histórico do sistema. Deseja
                  continuar?
                </Typography.Text>
              </Flex>
            ),
            onCancel: () => {
              setIsEditing(true);
            },
            onOk: () => {
              handleUpdateUserInfo();
            }
          });
        })
        .catch(() => {
          setIsEditing(true);
        });
    }
  }, [isEditing, modal, setIsEditing, handleUpdateUserInfo, form]);

  const cancelEdit = useCallback(() => {
    setIsEditing(false);
    form.resetFields();
  }, [setIsEditing, form]);

  return (
    <Form form={form}>
      <fieldset className="mb-6">
        <Flex
          justify="space-between"
          align="center"
          className="mb-6"
          gap={16}
          wrap="wrap"
        >
          <FieldsetTitle
            title="Dados do usuário"
            icon={Icons.UserOutlined}
            className="mb-0"
          />
          {enrollment?.certificate_status !== 'issued' && (
            <Space wrap size={12}>
              <Button
                variant="outlined"
                color="primary"
                icon={<Icons.EditOutlined />}
                loading={isLoading}
                disabled={isLoading}
                onClick={handleEdit}
              >
                {isEditing ? 'Salvar edição' : 'Editar'}
              </Button>

              {isEditing && (
                <Button
                  variant="outlined"
                  color="danger"
                  disabled={isLoading}
                  icon={<Icons.CloseOutlined />}
                  onClick={cancelEdit}
                >
                  Cancelar
                </Button>
              )}
            </Space>
          )}
        </Flex>
        <div className="ml-7">
          <FormItemCol
            label="Nome completo"
            name={['personal_info', 'full_name']}
            rules={[
              { required: true, message: 'Campo obrigatório' },
              fullNameValidatorRule()
            ]}
            initialValue={user?.personal_info?.full_name ?? ''}
          >
            <Input disabled={!isEditing} />
          </FormItemCol>
          <FormItemCol
            label="E-mail"
            name="email"
            rules={[{ required: true, message: 'E-mail é obrigatório' }]}
            initialValue={user?.email ?? ''}
          >
            <Input disabled={!isEditing} />
          </FormItemCol>
          <FormItemCol
            label="Telefone"
            name={['personal_info', 'phone_number']}
            rules={[
              { required: true, message: 'Telefone é obrigatório' },
              {
                pattern: /^\(\d{2}\)\s\d{8,9}$/,
                message: 'Telefone inválido'
              }
            ]}
            initialValue={user?.personal_info?.phone_number ?? ''}
          >
            <InputMask disabled={!isEditing} mask="(00) 000000000" />
          </FormItemCol>
        </div>
      </fieldset>

      {contextHolder}
    </Form>
  );
};
